import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { getDataFromSessionStorage } from "utils/helper";
import { useLogger } from "utils/logger";
import {
  getOnFidoApplicantId,
  getOnFidoSdkTokenAndWorkflowRunId,
  onFidoInitConfig,
  submitAndGetOnFidoCheckStatus,
} from "utils/onfido";

import { useRuleEngine } from "./useRuleEngine";

export function useOnfidoCheck({ journey, personalData, userAccountData }) {
  const router = useRouter();

  // Onboarding rule engine
  const {
    userIDVRequired,
    ruleEngineCompleted,
    ruleEngineInprogress,
    callRuleEngine,
    setCallRuleEngine,
  } = useRuleEngine({
    userAccountData,
  });

  const [onfidoSubmissionInProgress, setOnfidoSubmissionInProgress] =
    useState(null);
  const [onfidoSubmissionComplete, setOnfidoSubmissionComplete] =
    useState(false);
  const [userClearedOnfido, setUserClearedOnfido] = useState(null);

  const { log, error, warn } = useLogger();

  const [doIDV, triggerIDV] = useState(false);

  // Rule engine
  useEffect(() => {
    if (!callRuleEngine && doIDV) {
      setCallRuleEngine(true);
    }
  }, [callRuleEngine, doIDV]);

  // onfido check
  useEffect(() => {
    if (!doIDV) return;
    if (!ruleEngineCompleted || userIDVRequired === null) return; // rule engine has not completed yet

    if (ruleEngineCompleted && userIDVRequired === false) {
      setUserClearedOnfido(true);
      return;
    }

    if (onfidoSubmissionInProgress || onfidoSubmissionComplete) return;

    setOnfidoSubmissionInProgress(true);

    log("Onfido Started");

    async function onfidoCheck() {
      const applicantId = await getOnFidoApplicantId(
        personalData.firstName,
        personalData.lastName,
      );

      // Step 2: Generate SDK Token
      if (!applicantId) {
        error(
          `Onfido`,
          `Invalid applicantId=${applicantId}`,
          `${getDataFromSessionStorage("applicationTokens").uuid}`,
        );
        router.push("/500"); //TRY FAILED
        return;
      }

      const tokenAndWorkflowRunId = await getOnFidoSdkTokenAndWorkflowRunId(
        applicantId,
      );

      if (!tokenAndWorkflowRunId) {
        error(
          `Onfido`,
          `Unable to get SDK token or workflowRunId`,
          `${getDataFromSessionStorage("applicationTokens").uuid}`,
        );
        router.push("/500"); //TRY FAILED
        return;
      }

      // debug(`Onfido verification intialised`, `applicantId=${applicantId}`, `sdkToken=${sdkToken}` `${JSON.parse(Cookies.get('applicationTokens')).uuid}`

      // Step 3: Initializing the onfido SDK
      const onFidoComponent = await import("onfido-sdk-ui");
      const onfidoInstance = onFidoComponent.init({
        ...onFidoInitConfig,
        ...tokenAndWorkflowRunId,
        // Step 4: Handle callbacks
        onComplete: async function (_data) {
          onfidoInstance.setOptions({ isModalOpen: false });
          onfidoInstance.safeTearDown();
          setOnfidoSubmissionComplete(true);

          const onfidoVerificationStatus = await submitAndGetOnFidoCheckStatus(
            applicantId,
            router,
          );

          if (!onfidoVerificationStatus) {
            warn(`Prospect failed to pass Onfido verification`);
            router.push(`/${journey}/declined`, "/");
          } else {
            log(`Prospect passed Onfido verification`);
            setUserClearedOnfido(onfidoVerificationStatus);
          }
        },
        onModalRequestClose: async function () {
          onfidoInstance.setOptions({ isModalOpen: false });
          await onfidoInstance.safeTearDown();

          if (onfidoSubmissionComplete) return;
        },
        onUserExit: async function (_userExitCode) {
          onfidoInstance.setOptions({ isModalOpen: false });
          await onfidoInstance.safeTearDown();

          warn(`User exited onfido`);
        },
        steps: ["welcome", "document", "face", "complete"],
      });
    }

    onfidoCheck();
  }, [
    journey,
    onfidoSubmissionComplete,
    onfidoSubmissionInProgress,
    personalData.firstName,
    personalData.lastName,
    ruleEngineCompleted,
    userIDVRequired,
    error,
    log,
    warn,
  ]);

  return {
    userClearedOnfido,
    onfidoSubmissionComplete,
    onfidoSubmissionInProgress:
      onfidoSubmissionInProgress || ruleEngineInprogress,
    ruleEngineCompleted,
    userIDVRequired,
    doIDV,
    triggerIDV,
  };
}
